import { Input } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listDriveItemsOptions, type SharePointDrive, type SharePointItem, searchDriveItemsOptions } from "@/api";
import { InlineNotice } from "@/components/InlineNotice";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { FileRow, FolderRow, NoMatches, RowsSkeleton } from "./DriveRows";
import { PickerPath } from "./PickerPath";

export interface PickerSelection {
  selected: SharePointItem[];
  onToggle: (item: SharePointItem, on: boolean) => void;
  /** False for files chat doesn't take. */
  usable: (item: SharePointItem) => boolean;
}

/**
 * Folders and files of one drive (OneDrive, or a SharePoint document library). Folders open; files are ticked.
 * Search covers the whole drive, not only the open folder.
 */
export function DriveBrowser({
  drive,
  rootCrumbs,
  searchLabel,
  onExit,
  selection,
}: Readonly<{
  drive: SharePointDrive;
  /** The path down to the drive's root, e.g. ["My files"] or [site, library]. */
  rootCrumbs: string[];
  searchLabel: string;
  /** Back from the drive's root (to the libraries); none for OneDrive. */
  onExit?: () => void;
  selection: PickerSelection;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "cloud" });
  const [folders, setFolders] = useState<SharePointItem[]>([]);
  const [search, setSearch] = useState("");
  const query = useDebouncedValue(search.trim(), 250);
  const folder = folders.at(-1);

  const listing = useQuery({
    ...listDriveItemsOptions({ path: { drive_id: drive.id }, query: folder ? { folder_id: folder.id } : {} }),
    enabled: !query,
    select: (r) => r.items ?? [],
  });
  const results = useQuery({
    ...searchDriveItemsOptions({ path: { drive_id: drive.id }, query: { q: query } }),
    enabled: Boolean(query),
  });
  const active = query ? results : listing;
  const items = active.data ?? [];

  const open = (item: SharePointItem) => {
    setSearch("");
    setFolders((stack) => [...stack, item]);
  };
  const back = () => {
    if (search) setSearch("");
    else if (folders.length > 0) setFolders((stack) => stack.slice(0, -1));
    else onExit?.();
  };
  const canGoBack = Boolean(search) || folders.length > 0 || Boolean(onExit);

  return (
    <div className="flex flex-col gap-3">
      <PickerPath
        crumbs={
          search ? [t("searchResultsIn", { name: rootCrumbs.at(-1) })] : [...rootCrumbs, ...folders.map((f) => f.name)]
        }
        onBack={canGoBack ? back : undefined}
      />
      <Input
        size="md"
        leadingIcon={<Search />}
        placeholder={searchLabel}
        aria-label={searchLabel}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {active.isError && <InlineNotice tone="error">{t("loadFailed")}</InlineNotice>}
      {active.isPending && !active.isError && <RowsSkeleton />}
      {active.isSuccess && items.length === 0 && <NoMatches title={query ? t("noResults") : t("emptyFolder")} />}
      {active.isSuccess && items.length > 0 && (
        <ul className="flex flex-col">
          {items.map((item, i) =>
            item.is_folder ? (
              <FolderRow key={item.id} item={item} divider={i > 0} onOpen={() => open(item)} />
            ) : (
              <FileRow
                key={item.id}
                item={item}
                usable={selection.usable(item)}
                checked={selection.selected.some((f) => f.id === item.id)}
                divider={i > 0}
                onToggle={(on) => selection.onToggle(item, on)}
              />
            ),
          )}
        </ul>
      )}
    </div>
  );
}
